import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";

import LoadingSpinner from "../../common/components/loadingSpinner/LoadingSpinner";
import NoResultsFound from "../../common/components/noResultsFound/NoResultsFound";
import { fixedWidthColumnStyle } from "../../common/utils/common";
import { TicketTimeTracking } from "./TicketTimeTracking";
import { createTicketTimeTracking, deleteTicketTimeTracking, fetchTicketTimeTracking } from "./ticketTimeTrackingApi";
import TicketTimeTrackingForm from "./TicketTimeTrackingForm";
import { sumTotalTime } from "../../common/utils/dateHelper";
import TicketTimeTrackingTotalTimeLabel from "./TicketTimeTrackingTotalTimeLabel";

interface TicketTimeTrackingTableProps {
  departmentId: string;
  ticketId: string;
  ticketCompany: string;
}

export default function TicketTimeTrackingCard({ departmentId, ticketId, ticketCompany }: TicketTimeTrackingTableProps) {
  const [timeTrackings, setTimeTrackings] = useState<TicketTimeTracking[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    doFetchTicketTimeTrackings();
  }, [ticketId]);

  const onDelete = (timeTracking: TicketTimeTracking) => {
    confirmDialog({
      header: "Confirmare stergere",
      icon: "pi pi-exclamation-triangle",
      message: <div className="text-xl">Esti sigur ca vrei sa stergi activitatea?</div>,
      acceptLabel: "Da",
      rejectLabel: "Nu",
      accept: async () => {
        setIsLoading(true);
        await deleteTicketTimeTracking(timeTracking.id);
        await doFetchTicketTimeTrackings();
      },
    });
  };

  const onSubmit = async (ticketId: string, ticketTimeTracking: TicketTimeTracking) => {
    setIsLoading(true);
    await createTicketTimeTracking(ticketId, ticketTimeTracking);
    await doFetchTicketTimeTrackings();
  };

  const doFetchTicketTimeTrackings = async () => {
    const timeTrackings = await fetchTicketTimeTracking(ticketId);
    setTimeTrackings(timeTrackings);
    setIsLoading(false);
  };

  const renderTableFooter = () => {
    const sumOfTotalTime = sumTotalTime(timeTrackings.map((t) => t.totalTime));
    return (
      <div className="flex justify-content-between">
        <span>Total ore</span>
        <TicketTimeTrackingTotalTimeLabel hours={sumOfTotalTime.hours} minutes={sumOfTotalTime.minutes} />
      </div>
    );
  };

  return (
    <Card>
      <TicketTimeTrackingForm departmentId={departmentId} ticketId={ticketId} ticketCompany={ticketCompany} onSubmit={onSubmit} />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <DataTable size="small" value={timeTrackings} footer={renderTableFooter()} emptyMessage={<NoResultsFound />}>
          <Column header="Agent" className="w-12rem" field="userName"></Column>
          <Column header="Data" field="date" style={fixedWidthColumnStyle(90)}></Column>
          <Column
            header="Timp"
            body={(rowData) => (
              <>
                {rowData.startTime} - {rowData.endTime}
              </>
            )}
            style={fixedWidthColumnStyle(110)}
          ></Column>
          <Column
            header="Total ore"
            style={fixedWidthColumnStyle(100)}
            body={(rowData) => <TicketTimeTrackingTotalTimeLabel hours={rowData.totalTime.hours} minutes={rowData.totalTime.minutes} />}
          ></Column>
          <Column header="Descriere" field="description"></Column>
          <Column
            align="center"
            header="Facturabil"
            body={(rowData) => <i className={rowData.isBillable ? "pi pi-check text-green-500" : "pi pi-times text-red-500"} />}
          ></Column>
          <Column
            className="w-4rem"
            body={(rowData) => <Button size="small" severity="danger" icon="pi pi-trash" rounded text onClick={() => onDelete(rowData)} />}
          ></Column>
        </DataTable>
      )}
    </Card>
  );
}
